import HintText from '@Guidelines/Component/HintText';
import SectionCard from '@Guidelines/Component/SectionCard';
import type SubjectTypeDefinitionFormData from '@UI/Pages/Administration/SubjectTypes/SubjectTypeEdit/FormData/SubjectTypeDefinitionFormData';
import ArchivedFields from '@UI/Pages/Administration/SubjectTypes/SubjectTypeEdit/Sections/Sheet/ArchivedFields';
import FieldRow from '@UI/Pages/Administration/SubjectTypes/SubjectTypeEdit/Sections/Sheet/FieldRow';
import FieldSidebar from '@UI/Pages/Administration/SubjectTypes/SubjectTypeEdit/Sidebars/FieldSidebar';
import { openNewFieldInSidebar } from '@UI/Pages/Administration/SubjectTypes/SubjectTypeEdit/Signals/fieldSidebarSignal';
import { Plus } from 'lucide-react';
import * as React from 'react';
import { Button } from '@verclam/ui-kit';

type Props = {
	readonly formData: SubjectTypeDefinitionFormData;
	readonly onChange: (
		update: (formData: SubjectTypeDefinitionFormData) => SubjectTypeDefinitionFormData,
	) => void;
	readonly isReadOnly: boolean;
	readonly isUsed: boolean;
};

/** The sheet of the type: its active fields in order, the way to add one, and the archived ones. */
const SheetSection: React.FC<Props> = ({ formData, onChange, isReadOnly, isUsed }) => {
	const activeFields = React.useMemo(
		() =>
			formData.fields
				.map((field, index) => ({ field, index }))
				.filter((entry) => !entry.field.archived),
		[formData.fields],
	);

	return (
		<SectionCard
			title="Fiche"
			description="« Appliquer » met le brouillon à jour : seul « Enregistrer » enregistre le type."
		>
			{activeFields.map(({ field, index }, position) => (
				<FieldRow
					key={field.key ?? `new-${index}`}
					onChange={onChange}
					index={index}
					field={field}
					canMoveUp={position > 0}
					canMoveDown={position < activeFields.length - 1}
					isReadOnly={isReadOnly}
					serverMessage={formData.getFieldServerMessage(index)}
				/>
			))}
			<div>
				<Button
					type="button"
					size="sm"
					severity="secondary"
					icon={<Plus size={16} />}
					label="Ajouter un champ"
					disabled={isReadOnly}
					onClick={openNewFieldInSidebar}
				/>
			</div>
			<ArchivedFields formData={formData} onChange={onChange} isReadOnly={isReadOnly} />
			<HintText>Un champ ajouté s'affiche « Non renseigné » sur les dossiers existants.</HintText>
			<FieldSidebar formData={formData} onChange={onChange} isUsed={isUsed} />
		</SectionCard>
	);
};

export default SheetSection;
